import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SeoAnalysisDialog } from '@/components/articles/SeoAnalysisDialog';
import { EeatCheckerDialog } from '@/components/articles/EeatCheckerDialog';
import { HelpfulContentDialog } from '@/components/articles/HelpfulContentDialog';
import { KeyTakeawaysDrawer } from '@/components/articles/KeyTakeawaysDrawer';
import { ArticleFAQDrawer } from '@/components/articles/ArticleFAQDrawer';
import { ArticleSourcesDrawer } from '@/components/articles/ArticleSourcesDrawer';
import { useArticleEditor, ArticleEditorFormState } from '@/contexts/ArticleEditorContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useCMSSettings } from '@/hooks/useCMSSettings';
import { useArticle, useCreateArticle, useUpdateArticle, useArticleCategories, useArticleTags, extractInteractiveContent } from '@/hooks/useArticles';
import { analyzeArticleSEO, getScoreColor, getScoreBgColor } from '@/utils/seoAnalyzer';
import { generateCMSPreviewUrl } from '@/utils/urlUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AiInput } from '@/components/ui/ai-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  X, 
  Save, 
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Settings,
  Share2,
  Code,
  Target,
  Sparkles,
  Loader2,
  Image as ImageIcon,
  Trash2,
  User,
  BarChart3,
  Wand2,
  List,
  Link2,
  Plus,
  Trash2 as TrashIcon,
  PanelLeftClose,
  PanelRightClose,
  RefreshCw,
  HelpCircle,
  ChevronDown,
  Copy,
  Heart,
  BookOpen,
  ShieldQuestion,
} from 'lucide-react';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import { supabase } from '@/integrations/supabase/client';
import MediumStyleEditor from '@/components/articles/MediumStyleEditor';
import { ArticleMediaLibrary } from '@/components/articles/ArticleMediaLibrary';
import ArticleTagSelector from '@/components/articles/ArticleTagSelector';
import { ArticleAuthorSelect, MultiAuthorSelect } from '@/components/articles/ArticleAuthorSelect';
import type { Article, SEOCheck, ArticleSource, ArticleFAQ } from '@/types/articles';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import { markdownToHtml } from '@/utils/markdownToHtml';

const STATUS_OPTIONS = ['draft', 'preview', 'scheduled', 'published', 'archived'] as const;

const statusConfig = {
  draft: { label: 'Draft', dotColor: 'bg-gray-400' },
  preview: { label: 'Preview', dotColor: 'bg-amber-500' },
  scheduled: { label: 'Scheduled', dotColor: 'bg-blue-500' },
  published: { label: 'Published', dotColor: 'bg-green-500' },
  archived: { label: 'Archived', dotColor: 'bg-gray-400' },
};

const schemaTypes = [
  { value: 'Article', label: 'Article' },
  { value: 'BlogPosting', label: 'Blog Post' },
  { value: 'NewsArticle', label: 'News Article' },
  { value: 'TechArticle', label: 'Tech Article' },
  { value: 'HowTo', label: 'How-To Guide' },
  { value: 'FAQ', label: 'FAQ' },
];

const CheckItem = ({ check }: { check: SEOCheck }) => {
  const icons = {
    good: <CheckCircle className="h-4 w-4 text-green-600" />,
    warning: <AlertTriangle className="h-4 w-4 text-yellow-600" />,
    error: <AlertCircle className="h-4 w-4 text-red-600" />,
  };
  
  return (
    <div className="flex items-start gap-3 py-2">
      {icons[check.status]}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{check.label}</p>
        <p className="text-xs text-muted-foreground">{check.message}</p>
      </div>
    </div>
  );
};

const ScoreCircle = ({ score, label }: { score: number; label: string }) => (
  <div className="flex flex-col items-center">
    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${getScoreBgColor(score)} bg-opacity-20`}>
      <span className={`text-xl font-bold ${getScoreColor(score)}`}>{score}</span>
    </div>
    <span className="text-xs text-muted-foreground mt-1">{label}</span>
  </div>
);

export function GlobalArticleEditor() {
  const {
    isOpen,
    articleId,
    isNew,
    formState,
    activeTab,
    setFormState,
    setActiveTab,
    closeEditor,
    resetEditor,
    updateFormField,
  } = useArticleEditor();

  const { currentBrandId: selectedBrandId, currentBrand } = useBrandManager();
  const { settings: cmsSettings } = useCMSSettings();
  const { data: existingArticle, isLoading: isLoadingArticle } = useArticle(isNew ? undefined : articleId || undefined);
  const { data: categories = [] } = useArticleCategories(selectedBrandId);
  const { data: tags = [] } = useArticleTags(selectedBrandId);
  const createArticle = useCreateArticle();
  const updateArticle = useUpdateArticle();

  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingExcerpt, setIsGeneratingExcerpt] = useState(false);
  const [hasLoadedArticle, setHasLoadedArticle] = useState<string | null>(null);
  const [featuredImageLibraryOpen, setFeaturedImageLibraryOpen] = useState(false);
  const [ogImageLibraryOpen, setOgImageLibraryOpen] = useState(false);
  const [twitterImageLibraryOpen, setTwitterImageLibraryOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [tocPanelOpen, setTocPanelOpen] = useState(false);
  const [sourcesPanelOpen, setSourcesPanelOpen] = useState(false);
  const [faqPanelOpen, setFaqPanelOpen] = useState(false);
  const [rightSidebarTool, setRightSidebarTool] = useState<string | null>(null);
  const [seoDialogOpen, setSeoDialogOpen] = useState(false);
  const [seoDialogType, setSeoDialogType] = useState<'analyze' | 'optimize'>('analyze');
  const [seoDialogData, setSeoDialogData] = useState<any>(null);
  const [eeatDialogOpen, setEeatDialogOpen] = useState(false);
  const [eeatData, setEeatData] = useState<any>(null);
  const [isCheckingEeat, setIsCheckingEeat] = useState(false);
  const [isFixingEeat, setIsFixingEeat] = useState(false);
  const [helpfulDialogOpen, setHelpfulDialogOpen] = useState(false);
  const [helpfulData, setHelpfulData] = useState<any>(null);
  const [isCheckingHelpful, setIsCheckingHelpful] = useState(false);
  const [isFixingHelpful, setIsFixingHelpful] = useState(false);
  const [takeawaysDialogOpen, setTakeawaysDialogOpen] = useState(false);
  const [takeawaysData, setTakeawaysData] = useState<any>(null);
  const [isGeneratingTakeaways, setIsGeneratingTakeaways] = useState(false);

  // Debounced values for SEO analysis
  const debouncedTitle = useDebounce(formState.title, 500);
  const debouncedSlug = useDebounce(formState.slug, 500);
  const debouncedContent = useDebounce(formState.content, 500);
  const debouncedMetaTitle = useDebounce(formState.metaTitle, 500);
  const debouncedMetaDescription = useDebounce(formState.metaDescription, 500);
  const debouncedFocusKeyword = useDebounce(formState.focusKeyword, 500);

  // SEO Analysis
  const seoAnalysis = useMemo(() => {
    return analyzeArticleSEO(
      debouncedTitle,
      debouncedSlug,
      debouncedContent,
      debouncedMetaTitle,
      debouncedMetaDescription,
      debouncedFocusKeyword
    );
  }, [debouncedTitle, debouncedSlug, debouncedContent, debouncedMetaTitle, debouncedMetaDescription, debouncedFocusKeyword]);

  // Extract TOC headings from content with IDs for anchoring
  const tocHeadings = useMemo(() => {
    if (!formState.content) return [];
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(formState.content, 'text/html');
    const headings: { level: number; text: string; id: string }[] = [];
    
    doc.querySelectorAll('h1, h2, h3, h4').forEach((heading, index) => {
      const level = parseInt(heading.tagName.charAt(1));
      const text = heading.textContent?.trim() || '';
      if (text) {
        // Generate a slug-based ID for anchoring
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `heading-${index}`;
        headings.push({ level, text, id });
      }
    });
    
    return headings;
  }, [formState.content]);

  // Handle link added - add to sources
  const handleLinkAdded = useCallback((url: string, text: string) => {
    // Check if this URL already exists in sources
    const exists = formState.sources.some(source => source.link === url);
    if (!exists) {
      const newSource: ArticleSource = {
        title: text || url,
        description: '',
        link: url
      };
      updateFormField('sources', [...formState.sources, newSource]);
    }
  }, [formState.sources, updateFormField]);

  // Sync links from article content to sources
  const handleSyncLinksFromArticle = useCallback(() => {
    if (!formState.content) {
      toast.info('No content to extract links from');
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(formState.content, 'text/html');
    const links = doc.querySelectorAll('a[href]');
    
    let addedCount = 0;
    const newSources = [...formState.sources];
    
    links.forEach((link) => {
      const url = link.getAttribute('href');
      const text = link.textContent?.trim() || '';
      
      // Skip empty URLs, anchors, and mailto/tel links
      if (!url || url.startsWith('#') || url.startsWith('mailto:') || url.startsWith('tel:')) {
        return;
      }
      
      // Check if URL already exists in sources
      const exists = newSources.some(source => source.link === url);
      if (!exists) {
        newSources.push({
          title: text || url,
          description: '',
          link: url
        });
        addedCount++;
      }
    });
    
    if (addedCount > 0) {
      updateFormField('sources', newSources);
      toast.success(`Added ${addedCount} new source${addedCount > 1 ? 's' : ''} from article`);
    } else {
      toast.info('No new links found to add');
    }
  }, [formState.content, formState.sources, updateFormField]);

  // Handle TOC item click - scroll to heading
  const handleTocClick = useCallback((headingId: string) => {
    // Find the heading in the editor by its text content
    const editorContainer = document.querySelector('.medium-editor .ProseMirror');
    if (!editorContainer) return;
    
    const headings = editorContainer.querySelectorAll('h1, h2, h3, h4');
    for (const heading of headings) {
      const text = heading.textContent?.trim() || '';
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (id === headingId) {
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      }
    }
  }, []);

  // Load existing article data when editing
  useEffect(() => {
    if (existingArticle && articleId && hasLoadedArticle !== articleId) {
      // Convert markdown to HTML if needed
      const content = existingArticle.content || '';
      console.log('Loading article content:', { 
        contentLength: content.length, 
        contentPreview: content.substring(0, 100),
        articleId 
      });
      
      let htmlContent = markdownToHtml(content);
      
      // Unescape any escaped iframe tags that may have been stored as HTML entities
      // This handles cases where <iframe was stored as &lt;iframe ... &gt;&lt;/iframe&gt;
      htmlContent = htmlContent.replace(
        /&lt;iframe([^]*?)&gt;(&lt;\/iframe&gt;)?/gi,
        (match, attrs) => {
          // Unescape the attributes
          const unescapedAttrs = (attrs || '')
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&#39;/g, "'")
            .replace(/&apos;/g, "'");
          return `<iframe${unescapedAttrs}></iframe>`;
        }
      );
      
      console.log('After markdown conversion:', { 
        htmlLength: htmlContent.length, 
        htmlPreview: htmlContent.substring(0, 100) 
      });
      
      setFormState({
        title: existingArticle.title,
        slug: existingArticle.slug,
        content: htmlContent,
        excerpt: existingArticle.excerpt || '',
        focusKeyword: existingArticle.focus_keyword || '',
        metaTitle: existingArticle.meta_title || '',
        metaDescription: existingArticle.meta_description || '',
        status: existingArticle.status,
        categoryIds: existingArticle.category_ids || [],
        tagIds: existingArticle.tag_ids || [],
        featuredImageUrl: existingArticle.featured_image_url || '',
        featuredImageAlt: existingArticle.featured_image_alt || '',
        schemaType: existingArticle.schema_type,
        ogTitle: existingArticle.og_title || '',
        ogDescription: existingArticle.og_description || '',
        ogImage: existingArticle.og_image || '',
        twitterTitle: existingArticle.twitter_title || '',
        twitterDescription: existingArticle.twitter_description || '',
        twitterImage: existingArticle.twitter_image || '',
        canonicalUrl: existingArticle.canonical_url || '',
        isCornerstone: existingArticle.is_cornerstone,
        noindex: existingArticle.noindex,
        authorId: existingArticle.author_id || null,
        coAuthorIds: existingArticle.co_author_ids || [],
        expertReviewerIds: existingArticle.expert_reviewer_ids || [],
        sources: existingArticle.sources || [],
        faqs: existingArticle.faqs || [],
        keyTakeaways: existingArticle.key_takeaways || null,
      });
      setHasLoadedArticle(articleId);
    }
  }, [existingArticle, articleId, hasLoadedArticle, setFormState]);

  // Reset loaded article tracker when articleId changes to a new article
  useEffect(() => {
    if (isNew) {
      setHasLoadedArticle(null);
    }
  }, [isNew]);

  // Auto-generate slug from title for new articles
  useEffect(() => {
    if (isNew && formState.title && !formState.slug) {
      const generated = formState.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      updateFormField('slug', generated);
    }
  }, [formState.title, isNew, formState.slug, updateFormField]);

  // Auto-construct canonical URL from brand website + /articles/ + slug
  useEffect(() => {
    if (formState.slug && !formState.canonicalUrl && currentBrand?.website) {
      const baseUrl = currentBrand.website.replace(/\/$/, ''); // Remove trailing slash
      const canonicalUrl = `${baseUrl}/articles/${formState.slug}`;
      updateFormField('canonicalUrl', canonicalUrl);
    }
  }, [formState.slug, formState.canonicalUrl, currentBrand?.website, updateFormField]);

  const handleSave = async () => {
    if (!selectedBrandId) {
      toast.error('Please select a brand');
      return;
    }
    
    if (!formState.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setIsSaving(true);
    
    // Calculate word count and reading time
    const plainText = formState.content.replace(/<[^>]*>/g, '');
    const wordCount = plainText.trim().split(/\s+/).filter(w => w.length > 0).length;
    const readingTime = Math.ceil(wordCount / 200);

    // Auto-extract interactive content from HTML
    const interactiveContentIds = extractInteractiveContent(formState.content);
    console.log('[Article Save] Extracted interactive content:', interactiveContentIds, 'from content length:', formState.content?.length);

    const articleData = {
      title: formState.title,
      slug: formState.slug,
      content: formState.content,
      excerpt: formState.excerpt,
      focus_keyword: formState.focusKeyword || null,
      meta_title: formState.metaTitle || null,
      meta_description: formState.metaDescription || null,
      status: formState.status,
      category_ids: formState.categoryIds,
      tag_ids: formState.tagIds,
      featured_image_url: formState.featuredImageUrl || null,
      featured_image_alt: formState.featuredImageAlt || null,
      schema_type: formState.schemaType,
      og_title: formState.ogTitle || null,
      og_description: formState.ogDescription || null,
      og_image: formState.ogImage || null,
      twitter_title: formState.twitterTitle || null,
      twitter_description: formState.twitterDescription || null,
      twitter_image: formState.twitterImage || null,
      canonical_url: formState.canonicalUrl || null,
      is_cornerstone: formState.isCornerstone,
      noindex: formState.noindex,
      author_id: formState.authorId || null,
      co_author_ids: formState.coAuthorIds,
      expert_reviewer_ids: formState.expertReviewerIds,
      sources: formState.sources,
      faqs: formState.faqs,
      key_takeaways: formState.keyTakeaways,
      interactive_content_ids: interactiveContentIds,
      word_count: wordCount,
      reading_time_minutes: readingTime,
      seo_score: seoAnalysis.seoScore,
      readability_score: seoAnalysis.readabilityScore,
    };

    try {
      if (isNew) {
        await createArticle.mutateAsync({
          ...articleData,
          brand_id: selectedBrandId,
        });
        toast.success('Article created');
        resetEditor();
        closeEditor();
      } else {
        await updateArticle.mutateAsync({
          id: articleId!,
          ...articleData,
        });
        toast.success('Article saved');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateExcerpt = async () => {
    if (!formState.content || formState.content.replace(/<[^>]*>/g, '').trim().length < 50) {
      toast.error('Please add more content before generating an excerpt');
      return;
    }

    setIsGeneratingExcerpt(true);
    try {
      const { data, error } = await supabase.functions.invoke('suggest-article-excerpt', {
        body: {
          content: formState.content,
          title: formState.title,
          brandId: selectedBrandId
        }
      });

      if (error) throw error;
      
      if (data?.excerpt) {
        updateFormField('excerpt', data.excerpt);
        toast.success('Excerpt generated');
      } else if (data?.error) {
        toast.error(data.error);
      }
    } catch (error: any) {
      console.error('Error generating excerpt:', error);
      toast.error(error.message || 'Failed to generate excerpt');
    } finally {
      setIsGeneratingExcerpt(false);
    }
  };

  const handleSeoAnalyze = async () => {
    if (!formState.content || formState.content.trim().length < 50) {
      toast.error('Please add content (at least 50 characters) before analyzing');
      return;
    }

    if (!selectedBrandId) {
      toast.error('Please select a brand before analyzing');
      return;
    }

    setIsAnalyzing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get article URL if published, otherwise use content
      const articleUrl = formState.status === 'published' && formState.slug 
        ? `${window.location.origin}/blog/${formState.slug}`
        : undefined;

      const { data, error } = await supabase.functions.invoke('seo-machine', {
        body: {
          action: 'analyze',
          url: articleUrl,
          filePath: articleId ? `articles/${articleId}.md` : undefined,
          content: formState.content,
          title: formState.title,
          brandId: selectedBrandId,
          userId: user.id
        }
      });

      if (error) {
        console.error('SEO Machine analyze error:', error);
        throw new Error(error.message || 'Failed to analyze content. Make sure the SEO Machine function is deployed.');
      }

      if (data) {
        setSeoDialogType('analyze');
        setSeoDialogData(data);
        setSeoDialogOpen(true);
        
        const providerInfo = data.modelUsed ? ` (via ${data.modelUsed})` : '';
        if (data.contentHealthScore !== null) {
          toast.success(`Analysis complete! Score: ${data.contentHealthScore}/100${providerInfo}`);
        } else {
          toast.success(`Analysis complete!${providerInfo}`);
        }
      }
    } catch (error: any) {
      console.error('Error analyzing article:', error);
      toast.error(error.message || 'Failed to analyze article');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSeoOptimize = async () => {
    if (!formState.content || formState.content.trim().length < 50) {
      toast.error('Please add content (at least 50 characters) before optimizing');
      return;
    }

    if (!selectedBrandId) {
      toast.error('Please select a brand before optimizing');
      return;
    }

    setIsOptimizing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('seo-machine', {
        body: {
          action: 'optimize',
          filePath: articleId ? `articles/${articleId}.md` : undefined,
          content: formState.content,
          title: formState.title,
          brandId: selectedBrandId,
          userId: user.id
        }
      });

      if (error) {
        console.error('SEO Machine optimize error:', error);
        throw new Error(error.message || 'Failed to optimize content. Make sure the SEO Machine function is deployed.');
      }

      if (data) {
        // Apply meta improvements automatically
        if (data.metaTitle) {
          updateFormField('metaTitle', data.metaTitle);
        }
        if (data.metaDescription) {
          updateFormField('metaDescription', data.metaDescription);
        }
        
        // Show results in modal
        setSeoDialogType('optimize');
        setSeoDialogData(data);
        setSeoDialogOpen(true);
        
        const providerInfo = data.modelUsed ? ` (via ${data.modelUsed})` : '';
        if (data.seoScore !== null) {
          toast.success(`Optimization complete! SEO Score: ${data.seoScore}/100${providerInfo}`);
        } else {
          toast.success(`Optimization complete!${providerInfo}`);
        }
      }
    } catch (error: any) {
      console.error('Error optimizing article:', error);
      toast.error(error.message || 'Failed to optimize article');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleEeatCheck = async () => {
    if (!formState.content || formState.content.trim().length < 50) {
      toast.error('Please add content (at least 50 characters) before checking EEAT');
      return;
    }

    setIsCheckingEeat(true);
    try {
      const { data, error } = await supabase.functions.invoke('eeat-checker', {
        body: {
          content: formState.content,
          title: formState.title,
          brandId: selectedBrandId,
          action: 'check',
        }
      });

      if (error) throw error;

      if (data?.success) {
        setEeatData(data);
        setEeatDialogOpen(true);
        toast.success(`EEAT Check complete! Score: ${data.averageScore}/100`);
      } else if (data?.error) {
        toast.error(data.error);
      }
    } catch (error: any) {
      console.error('EEAT check error:', error);
      toast.error(error.message || 'Failed to run EEAT check');
    } finally {
      setIsCheckingEeat(false);
    }
  };

  const handleEeatFix = async () => {
    if (!formState.content || formState.content.trim().length < 50) {
      toast.error('No content to fix');
      return;
    }

    setIsFixingEeat(true);
    try {
      const { data, error } = await supabase.functions.invoke('eeat-checker', {
        body: {
          content: formState.content,
          title: formState.title,
          brandId: selectedBrandId,
          action: 'fix',
        }
      });

      if (error) throw error;

      if (data?.rewrittenContent) {
        updateFormField('content', data.rewrittenContent);
        setEeatDialogOpen(false);
        toast.success('Article rewritten with improved EEAT scores! Review the changes.');
      } else if (data?.error) {
        toast.error(data.error);
      }
    } catch (error: any) {
      console.error('EEAT fix error:', error);
      toast.error(error.message || 'Failed to fix article');
    } finally {
      setIsFixingEeat(false);
    }
  };

  // Helpful Content Check
  const handleHelpfulCheck = async () => {
    if (!formState.content || formState.content.trim().length < 50) {
      toast.error('Please add content (at least 50 characters) before checking');
      return;
    }
    setIsCheckingHelpful(true);
    try {
      const { data, error } = await supabase.functions.invoke('helpful-content-checker', {
        body: { content: formState.content, title: formState.title, brandId: selectedBrandId, action: 'check' }
      });
      if (error) throw error;
      if (data?.success) {
        setHelpfulData(data);
        setHelpfulDialogOpen(true);
        toast.success(`Helpful Content Check complete! Score: ${data.averageScore}/100`);
      } else if (data?.error) { toast.error(data.error); }
    } catch (error: any) {
      console.error('Helpful content check error:', error);
      toast.error(error.message || 'Failed to run check');
    } finally { setIsCheckingHelpful(false); }
  };

  const handleHelpfulFix = async () => {
    if (!formState.content || formState.content.trim().length < 50) { toast.error('No content to fix'); return; }
    setIsFixingHelpful(true);
    try {
      const { data, error } = await supabase.functions.invoke('helpful-content-checker', {
        body: { content: formState.content, title: formState.title, brandId: selectedBrandId, action: 'fix' }
      });
      if (error) throw error;
      if (data?.rewrittenContent) {
        updateFormField('content', data.rewrittenContent);
        setHelpfulDialogOpen(false);
        toast.success('Article rewritten with improved helpfulness! Review the changes.');
      } else if (data?.error) { toast.error(data.error); }
    } catch (error: any) {
      console.error('Helpful fix error:', error);
      toast.error(error.message || 'Failed to fix article');
    } finally { setIsFixingHelpful(false); }
  };

  // Key Takeaways Generator
  const handleKeyTakeaways = async () => {
    if (!formState.content || formState.content.trim().length < 50) {
      toast.error('Please add content (at least 50 characters) before generating takeaways');
      return;
    }
    setIsGeneratingTakeaways(true);
    try {
      const { data, error } = await supabase.functions.invoke('key-takeaways-generator', {
        body: { content: formState.content, title: formState.title, brandId: selectedBrandId }
      });
      if (error) throw error;
      if (data?.success) {
        setTakeawaysData(data);
        setTakeawaysDialogOpen(true);
        toast.success('Key takeaways generated!');
      } else if (data?.error) { toast.error(data.error); }
    } catch (error: any) {
      console.error('Key takeaways error:', error);
      toast.error(error.message || 'Failed to generate takeaways');
    } finally { setIsGeneratingTakeaways(false); }
  };

  // Handle adding takeaways to article content + saving to DB
  const handleAddTakeawaysToArticle = (takeawaysPayload: import('@/types/articles').ArticleKeyTakeaways, placement: 'top' | 'bottom') => {
    const takeawaysHtml = `<div class="key-takeaways-box">
<h4 style="margin: 0 0 0.5rem 0; font-size: 0.8rem; font-weight: 600;">✨ Key Takeaways</h4>
<p style="font-size: 0.75rem; color: #444; margin: 0 0 0.4rem 0; line-height: 1.5;">${takeawaysPayload.summary}</p>
<ul style="margin: 0; padding-left: 1.1rem; font-size: 0.75rem; line-height: 1.5;">
${takeawaysPayload.takeaways.map(t => `<li style="margin-bottom: 0.25rem; font-size: 0.75rem;"><strong>${t.title}</strong> — ${t.description}</li>`).join('\n')}
</ul>
<p style="font-size: 0.65rem; color: #666; margin: 0.4rem 0 0 0; font-style: italic;">Reading time: ${takeawaysPayload.readingTime} · Audience: ${takeawaysPayload.targetAudience} · Level: ${takeawaysPayload.difficulty}</p>
</div>`;

    const currentContent = formState.content || '';
    const newContent = placement === 'top'
      ? takeawaysHtml + '\n' + currentContent
      : currentContent + '\n' + takeawaysHtml;

    updateFormField('content', newContent);
    updateFormField('keyTakeaways', { ...takeawaysPayload, placement });
  };

  const keyphraseChecks = seoAnalysis.checks.filter(c => c.category === 'keyphrase');
  const contentChecks = seoAnalysis.checks.filter(c => c.category === 'content');
  const readabilityChecks = seoAnalysis.checks.filter(c => c.category === 'readability');

  const toggleRightTool = (tool: string) => {
    setRightSidebarTool(prev => prev === tool ? null : tool);
  };

  const rightTools = [
    { id: 'eeat', label: 'EEAT Checker', icon: CheckCircle },
    { id: 'helpful', label: 'Help-Full Content', icon: Heart },
    { id: 'takeaways', label: 'Key Takeaways', icon: BookOpen },
    { id: 'faqs', label: 'FAQs', icon: ShieldQuestion },
    { id: 'sources', label: 'Sources', icon: Link2 },
    { id: 'seo-analyze', label: 'SEO Analyze', icon: BarChart3 },
    { id: 'seo-optimize', label: 'SEO Optimize', icon: Wand2 },
    { id: 'internal-links', label: 'Internal Links', icon: Link2 },
  ];

  return (
    <>
      {/* Static fixed panel — no vaul drag */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeEditor}
          />
          {/* Panel */}
          <div className="relative ml-auto flex h-full w-full bg-background border-l shadow-2xl overflow-hidden" style={{ maxWidth: '100vw' }}>
        {!selectedBrandId ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Please select a brand to create articles.</p>
          </div>
        ) : !isNew && isLoadingArticle ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-background shrink-0">
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <Button variant="ghost" size="icon" onClick={closeEditor} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
                <div className="min-w-0">
                  <h1 className="text-lg font-semibold truncate">{isNew ? 'New Article' : 'Edit Article'}</h1>
                  <p className="text-sm text-muted-foreground truncate">/{formState.slug || 'untitled'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {/* TOC Panel Toggle */}
                <Button
                  variant={tocPanelOpen ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTocPanelOpen(!tocPanelOpen)}
                  title="Table of Contents"
                >
                  <List className="h-4 w-4 mr-1" />
                  TOC
                  {tocHeadings.length > 0 && (
                    <span className="ml-1 bg-primary/20 text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                      {tocHeadings.length}
                    </span>
                  )}
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-[160px] justify-between h-9 px-3 shrink-0"
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex items-center -space-x-1">
                          {STATUS_OPTIONS.map((s) => (
                            <div
                              key={s}
                              className={`h-3 w-3 rounded-full shrink-0 border border-background ${
                                formState.status === s
                                  ? statusConfig[s].dotColor
                                  : 'bg-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-sm font-medium">{statusConfig[formState.status]?.label ?? formState.status}</span>
                      </div>
                      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-0" align="start">
                    <div className="p-2">
                      {STATUS_OPTIONS.map((status) => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => updateFormField('status', status as Article['status'])}
                          className="flex items-center gap-3 py-2 px-2 rounded-md cursor-pointer hover:bg-muted/50 w-full text-left"
                        >
                          <div
                            className={`h-2.5 w-2.5 rounded-full shrink-0 ${statusConfig[status].dotColor}`}
                          />
                          <span className="text-sm">{statusConfig[status].label}</span>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button onClick={handleSave} disabled={isSaving} className="ml-2 shrink-0">
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                </div>
            </div>

            {/* Main Content Area (editor + right sidebar) */}
            <div className="flex flex-1 min-w-0 overflow-hidden">
            {/* Main Content with Resizable Panels */}
            <ResizablePanelGroup direction="horizontal" className="min-w-0 flex-1">
              {/* TOC Panel */}
              {tocPanelOpen && (
                <>
                  <ResizablePanel defaultSize={15} minSize={10} maxSize={25}>
                    <div className="h-full flex flex-col bg-muted/30">
                      <div className="p-3 border-b bg-background flex items-center justify-between shrink-0">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          <List className="h-4 w-4" />
                          Table of Contents
                        </h4>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => setTocPanelOpen(false)}
                        >
                          <PanelLeftClose className="h-4 w-4" />
                        </Button>
                      </div>
                      <ScrollArea className="flex-1">
                        <div className="p-3 space-y-1">
                          {tocHeadings.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No headings found. Add H1-H4 headings to your content.</p>
                          ) : (
                            tocHeadings.map((heading, index) => (
                              <div
                                key={index}
                                className="text-sm py-1 hover:bg-accent rounded px-2 cursor-pointer transition-colors"
                                style={{ paddingLeft: `${(heading.level - 1) * 12 + 8}px` }}
                                onClick={() => handleTocClick(heading.id)}
                              >
                                <span className="text-muted-foreground mr-1 text-xs">H{heading.level}</span>
                                <span>{heading.text}</span>
                              </div>
                            ))
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  </ResizablePanel>
                  <ResizableHandle withHandle />
                </>
              )}

              {/* Editor Section */}
              <ResizablePanel defaultSize={tocPanelOpen ? 50 : 60} minSize={30}>
                <div className="h-full overflow-y-auto p-6">
                  <div className="max-w-4xl mx-auto space-y-6">
                    {/* Title */}
                    <Input
                      placeholder="Article title"
                      value={formState.title}
                      onChange={(e) => updateFormField('title', e.target.value)}
                      className="text-2xl font-semibold h-auto py-3 border-0 border-b rounded-none px-0 focus-visible:ring-0"
                    />
                    
                    {/* Slug */}
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Slug:</span>
                      <Input
                        value={formState.slug}
                        onChange={(e) => updateFormField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                        className="h-8 max-w-md"
                      />
                    </div>
                    {formState.slug && selectedBrandId && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="shrink-0">Crawlable link:</span>
                        <code className="flex-1 min-w-0 truncate text-xs bg-muted px-2 py-1 rounded">
                          {(import.meta.env.VITE_SUPABASE_URL || 'https://hjodtxoylcvcwjgvuctk.supabase.co')}/functions/v1/article-detail-html?brand_id={selectedBrandId}&slug={encodeURIComponent(formState.slug)}
                        </code>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => {
                            const url = `${import.meta.env.VITE_SUPABASE_URL || 'https://hjodtxoylcvcwjgvuctk.supabase.co'}/functions/v1/article-detail-html?brand_id=${selectedBrandId}&slug=${encodeURIComponent(formState.slug)}`;
                            navigator.clipboard.writeText(url);
                            toast.success('Crawlable link copied');
                          }}
                          title="Copy crawlable link"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    )}

                    {/* Content Editor */}
                    <div className="min-h-[400px]">
                      <MediumStyleEditor
                        value={formState.content}
                        onChange={(value) => updateFormField('content', value)}
                        placeholder="Tell your story..."
                        onLinkAdded={handleLinkAdded}
                      />
                    </div>
                  </div>
                </div>
              </ResizablePanel>

              {/* Sources Panel removed - now in drawer */}


              <ResizableHandle withHandle />

              {/* SEO Sidebar */}
              <ResizablePanel defaultSize={25} minSize={20} maxSize={35}>
                <div className="h-full bg-muted/30 flex flex-col overflow-hidden">
                  {/* Scores */}
                  <div className="p-4 border-b bg-background shrink-0">
                    <div className="flex justify-around">
                      <ScoreCircle score={seoAnalysis.seoScore} label="SEO Score" />
                      <ScoreCircle score={seoAnalysis.readabilityScore} label="Readability" />
                    </div>
                  </div>

                  {/* Tabs */}
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                    <TabsList className="w-full justify-start rounded-none border-b bg-background px-4 shrink-0 overflow-x-auto overflow-y-hidden">
                      <TabsTrigger value="general" className="gap-2">
                        <Settings className="h-3.5 w-3.5" />
                        General
                      </TabsTrigger>
                      <TabsTrigger value="author" className="gap-2">
                        <User className="h-3.5 w-3.5" />
                        Author
                      </TabsTrigger>
                      <TabsTrigger value="seo" className="gap-2">
                        <Target className="h-3.5 w-3.5" />
                        SEO
                      </TabsTrigger>
                      <TabsTrigger value="social" className="gap-2">
                        <Share2 className="h-3.5 w-3.5" />
                        Social
                      </TabsTrigger>
                      <TabsTrigger value="schema" className="gap-2">
                        <Code className="h-3.5 w-3.5" />
                        Schema
                      </TabsTrigger>
                    </TabsList>

                    <div className="flex-1 overflow-y-auto">
                      {/* General Tab */}
                      <TabsContent value="general" className="m-0 p-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Focus Keyphrase</Label>
                          <Input
                            placeholder="Enter focus keyphrase"
                            value={formState.focusKeyword}
                            onChange={(e) => updateFormField('focusKeyword', e.target.value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Excerpt</Label>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleGenerateExcerpt}
                              disabled={isGeneratingExcerpt || !formState.content}
                              className="h-7 px-2 text-muted-foreground hover:text-primary"
                            >
                              {isGeneratingExcerpt ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Sparkles className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <Textarea
                            placeholder="Brief summary of the article"
                            value={formState.excerpt}
                            onChange={(e) => updateFormField('excerpt', e.target.value)}
                            rows={3}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Featured Image</Label>
                          {formState.featuredImageUrl ? (
                            <div className="space-y-3">
                              <div className="relative rounded-lg overflow-hidden border bg-muted">
                                <img 
                                  src={formState.featuredImageUrl} 
                                  alt={formState.featuredImageAlt || 'Featured image'}
                                  className="w-full h-40 object-cover"
                                />
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="absolute top-2 right-2 h-7 w-7"
                                  onClick={() => {
                                    updateFormField('featuredImageUrl', '');
                                    updateFormField('featuredImageAlt', '');
                                  }}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs">Alt Text</Label>
                                <Input
                                  placeholder="Describe the image for accessibility"
                                  value={formState.featuredImageAlt}
                                  onChange={(e) => updateFormField('featuredImageAlt', e.target.value)}
                                  className="h-8 text-sm"
                                />
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              className="w-full h-24 border-dashed flex-col gap-2"
                              onClick={() => setFeaturedImageLibraryOpen(true)}
                            >
                              <ImageIcon className="h-6 w-6 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Select from Media Library</span>
                            </Button>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Categories</Label>
                          <Select
                            value={formState.categoryIds[0] || ''}
                            onValueChange={(v) => updateFormField('categoryIds', v ? [v] : [])}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map(cat => (
                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Tags</Label>
                          <ArticleTagSelector
                            selectedTagIds={formState.tagIds}
                            onTagsChange={(tags) => updateFormField('tagIds', tags)}
                          />
                        </div>

                        <Separator />

                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Cornerstone Content</Label>
                            <p className="text-xs text-muted-foreground">Mark as important pillar content</p>
                          </div>
                          <Switch checked={formState.isCornerstone} onCheckedChange={(v) => updateFormField('isCornerstone', v)} />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <Label>No Index</Label>
                            <p className="text-xs text-muted-foreground">Hide from search engines</p>
                          </div>
                          <Switch checked={formState.noindex} onCheckedChange={(v) => updateFormField('noindex', v)} />
                        </div>

                        {/* API & Preview Links */}
                        {formState.slug && selectedBrandId && (
                          <div className="flex items-center justify-between pt-4">
                            <a
                              href={`${import.meta.env.VITE_SUPABASE_URL || 'https://hjodtxoylcvcwjgvuctk.supabase.co'}/functions/v1/article-detail-api?brand_id=${selectedBrandId}&slug=${formState.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:text-primary underline"
                            >
                              View JSON API
                            </a>
                            {cmsSettings?.site_url && (
                              <a
                                href={generateCMSPreviewUrl(
                                  cmsSettings.site_url,
                                  cmsSettings.url_prefixes || null,
                                  'article',
                                  formState.slug
                                ) || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-muted-foreground hover:text-primary underline"
                              >
                                Preview Article
                              </a>
                            )}
                          </div>
                        )}
                      </TabsContent>

                      {/* SEO Tab */}
                      <TabsContent value="seo" className="m-0 p-4 space-y-4">
                        {/* SEO Machine Actions */}
                        <div className="space-y-3 pb-4 border-b">
                          <Label className="text-sm font-semibold flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            SEO Machine
                          </Label>
                          <div className="flex flex-col gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleSeoAnalyze}
                              disabled={isAnalyzing || !formState.content || formState.content.trim().length < 50}
                              className="w-full justify-start"
                            >
                              {isAnalyzing ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Analyzing...
                                </>
                              ) : (
                                <>
                                  <BarChart3 className="h-4 w-4 mr-2" />
                                  Analyze Content
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleSeoOptimize}
                              disabled={isOptimizing || !formState.content || formState.content.trim().length < 50}
                              className="w-full justify-start"
                            >
                              {isOptimizing ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Optimizing...
                                </>
                              ) : (
                                <>
                                  <Wand2 className="h-4 w-4 mr-2" />
                                  Optimize SEO
                                </>
                              )}
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Meta Title</Label>
                          <Input
                            placeholder="SEO title (50-60 characters)"
                            value={formState.metaTitle}
                            onChange={(e) => updateFormField('metaTitle', e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            {formState.metaTitle.length}/60 characters
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Meta Description</Label>
                          <Textarea
                            placeholder="SEO description (150-160 characters)"
                            value={formState.metaDescription}
                            onChange={(e) => updateFormField('metaDescription', e.target.value)}
                            rows={3}
                          />
                          <p className="text-xs text-muted-foreground">
                            {formState.metaDescription.length}/160 characters
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Canonical URL</Label>
                          <Input
                            placeholder="https://..."
                            value={formState.canonicalUrl}
                            onChange={(e) => updateFormField('canonicalUrl', e.target.value)}
                          />
                        </div>

                        <Separator />

                        {/* Keyphrase Analysis */}
                        <div>
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <Target className="h-4 w-4" />
                            Keyphrase Analysis
                          </h4>
                          <div className="space-y-1">
                            {keyphraseChecks.map((check, i) => (
                              <CheckItem key={i} check={check} />
                            ))}
                          </div>
                        </div>

                        <Separator />

                        {/* Content Analysis */}
                        <div>
                          <h4 className="font-medium mb-2">Content Analysis</h4>
                          <div className="space-y-1">
                            {contentChecks.map((check, i) => (
                              <CheckItem key={i} check={check} />
                            ))}
                          </div>
                        </div>

                        <Separator />

                        {/* Readability */}
                        <div>
                          <h4 className="font-medium mb-2">Readability</h4>
                          <div className="space-y-1">
                            {readabilityChecks.map((check, i) => (
                              <CheckItem key={i} check={check} />
                            ))}
                          </div>
                        </div>
                      </TabsContent>

                      {/* Author Tab */}
                      <TabsContent value="author" className="m-0 p-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Primary Author</Label>
                          <ArticleAuthorSelect
                            brandId={selectedBrandId}
                            value={formState.authorId}
                            onChange={(value) => updateFormField('authorId', value)}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Co-Authors</Label>
                          <MultiAuthorSelect
                            brandId={selectedBrandId}
                            values={formState.coAuthorIds}
                            onChange={(ids) => updateFormField('coAuthorIds', ids)}
                            placeholder="Add co-authors..."
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Expert Reviewers</Label>
                          <MultiAuthorSelect
                            brandId={selectedBrandId}
                            values={formState.expertReviewerIds}
                            onChange={(ids) => updateFormField('expertReviewerIds', ids)}
                            placeholder="Add expert reviewers..."
                            expertsOnly
                          />
                          <p className="text-xs text-muted-foreground">
                            Expert reviewers are displayed for E-E-A-T compliance
                          </p>
                        </div>
                      </TabsContent>

                      {/* Social Tab */}
                      <TabsContent value="social" className="m-0 p-4 space-y-4">
                        <div className="space-y-4">
                          <h4 className="font-medium">Open Graph</h4>
                          <div className="space-y-2">
                            <Label>OG Title</Label>
                            <Input
                              placeholder="Title for social sharing"
                              value={formState.ogTitle}
                              onChange={(e) => updateFormField('ogTitle', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>OG Description</Label>
                            <Textarea
                              placeholder="Description for social sharing"
                              value={formState.ogDescription}
                              onChange={(e) => updateFormField('ogDescription', e.target.value)}
                              rows={2}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>OG Image</Label>
                            {formState.ogImage ? (
                              <div className="relative rounded-lg overflow-hidden border bg-muted">
                                <img 
                                  src={formState.ogImage} 
                                  alt="OG image"
                                  className="w-full h-32 object-cover"
                                />
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="absolute top-2 right-2 h-7 w-7"
                                  onClick={() => updateFormField('ogImage', '')}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                className="w-full h-16 border-dashed"
                                onClick={() => setOgImageLibraryOpen(true)}
                              >
                                <ImageIcon className="h-5 w-5 mr-2 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">Select OG Image</span>
                              </Button>
                            )}
                          </div>
                        </div>

                        <Separator />

                        <div className="space-y-4">
                          <h4 className="font-medium">Twitter Card</h4>
                          <div className="space-y-2">
                            <Label>Twitter Title</Label>
                            <Input
                              placeholder="Title for Twitter"
                              value={formState.twitterTitle}
                              onChange={(e) => updateFormField('twitterTitle', e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Twitter Description</Label>
                            <Textarea
                              placeholder="Description for Twitter"
                              value={formState.twitterDescription}
                              onChange={(e) => updateFormField('twitterDescription', e.target.value)}
                              rows={2}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Twitter Image</Label>
                            {formState.twitterImage ? (
                              <div className="relative rounded-lg overflow-hidden border bg-muted">
                                <img 
                                  src={formState.twitterImage} 
                                  alt="Twitter image"
                                  className="w-full h-32 object-cover"
                                />
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="absolute top-2 right-2 h-7 w-7"
                                  onClick={() => updateFormField('twitterImage', '')}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                className="w-full h-16 border-dashed"
                                onClick={() => setTwitterImageLibraryOpen(true)}
                              >
                                <ImageIcon className="h-5 w-5 mr-2 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">Select Twitter Image</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </TabsContent>

                      {/* Schema Tab */}
                      <TabsContent value="schema" className="m-0 p-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Schema Type</Label>
                          <Select value={formState.schemaType} onValueChange={(v) => updateFormField('schemaType', v as Article['schema_type'])}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {schemaTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Separator />

                        {/* Schema Preview */}
                        <div>
                          <h4 className="font-medium mb-2">Schema Preview</h4>
                          <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-[200px]">
{JSON.stringify({
  "@context": "https://schema.org",
  "@type": formState.schemaType,
  "headline": formState.metaTitle || formState.title,
  "description": formState.metaDescription,
  "image": formState.featuredImageUrl || formState.ogImage,
  "datePublished": new Date().toISOString(),
}, null, 2)}
                          </pre>
                        </div>
                      </TabsContent>
                    </div>
                  </Tabs>
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>

            {/* Right Icon Sidebar */}
            <TooltipProvider delayDuration={200}>
              <div className="flex flex-col border-l bg-muted/20 w-12 shrink-0">
                {rightTools.map(tool => (
                  <Tooltip key={tool.id}>
                    <TooltipTrigger asChild>
                      <button
                        disabled={
                          (tool.id === 'eeat' && isCheckingEeat) ||
                          (tool.id === 'helpful' && isCheckingHelpful) ||
                          (tool.id === 'takeaways' && isGeneratingTakeaways) ||
                          (tool.id === 'seo-analyze' && isAnalyzing) ||
                          (tool.id === 'seo-optimize' && isOptimizing)
                        }
                        onClick={() => {
                          if (tool.id === 'seo-analyze') { handleSeoAnalyze(); }
                          else if (tool.id === 'seo-optimize') { handleSeoOptimize(); }
                          else if (tool.id === 'eeat') { handleEeatCheck(); }
                          else if (tool.id === 'helpful') { handleHelpfulCheck(); }
                          else if (tool.id === 'seo-analyze') { 
                            if (seoDialogData && seoDialogType === 'analyze') { setSeoDialogOpen(!seoDialogOpen); } 
                            else { handleSeoAnalyze(); } 
                          }
                          else if (tool.id === 'seo-optimize') { 
                            if (seoDialogData && seoDialogType === 'optimize') { setSeoDialogOpen(!seoDialogOpen); } 
                            else { handleSeoOptimize(); } 
                          }
                          else if (tool.id === 'takeaways') { 
                            if (takeawaysData) { setTakeawaysDialogOpen(!takeawaysDialogOpen); } 
                            else { handleKeyTakeaways(); } 
                          }
                          else if (tool.id === 'faqs') { setFaqPanelOpen(!faqPanelOpen); }
                          else if (tool.id === 'sources') { setSourcesPanelOpen(!sourcesPanelOpen); }
                          else { toggleRightTool(tool.id); }
                        }}
                        className={`flex items-center justify-center h-12 w-12 border-b transition-colors hover:bg-accent disabled:opacity-50 disabled:pointer-events-none ${
                          rightSidebarTool === tool.id 
                          || (tool.id === 'takeaways' && takeawaysDialogOpen) 
                          || (tool.id === 'faqs' && faqPanelOpen)
                          || (tool.id === 'sources' && sourcesPanelOpen)
                          || (tool.id === 'seo-analyze' && seoDialogOpen && seoDialogType === 'analyze')
                          || (tool.id === 'seo-optimize' && seoDialogOpen && seoDialogType === 'optimize')
                            ? 'bg-accent text-primary' : 'text-muted-foreground'
                        }`}
                      >
                        {(tool.id === 'eeat' && isCheckingEeat) || (tool.id === 'helpful' && isCheckingHelpful) || (tool.id === 'takeaways' && isGeneratingTakeaways) || (tool.id === 'seo-analyze' && isAnalyzing) || (tool.id === 'seo-optimize' && isOptimizing)
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <tool.icon className="h-4 w-4" />
                        }
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{tool.label}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
          </div>
          </div>
        )}
      </div>
    </div>
  )}

      {/* Featured Image Media Library */}
      <ArticleMediaLibrary
        open={featuredImageLibraryOpen}
        onOpenChange={setFeaturedImageLibraryOpen}
        onSelect={(url, alt) => {
          updateFormField('featuredImageUrl', url);
          if (alt) updateFormField('featuredImageAlt', alt);
        }}
      />

      {/* OG Image Media Library */}
      <ArticleMediaLibrary
        open={ogImageLibraryOpen}
        onOpenChange={setOgImageLibraryOpen}
        onSelect={(url) => {
          updateFormField('ogImage', url);
        }}
      />

      {/* Twitter Image Media Library */}
      <ArticleMediaLibrary
        open={twitterImageLibraryOpen}
        onOpenChange={setTwitterImageLibraryOpen}
        onSelect={(url) => {
          updateFormField('twitterImage', url);
        }}
      />

      {/* SEO Analysis/Optimization Results Dialog */}
      <SeoAnalysisDialog
        open={seoDialogOpen}
        onOpenChange={setSeoDialogOpen}
        type={seoDialogType}
        data={seoDialogData}
      />

      {/* EEAT Checker Dialog */}
      <EeatCheckerDialog
        open={eeatDialogOpen}
        onOpenChange={setEeatDialogOpen}
        data={eeatData}
        onFix={handleEeatFix}
        isFixing={isFixingEeat}
      />

      {/* Helpful Content Dialog */}
      <HelpfulContentDialog
        open={helpfulDialogOpen}
        onOpenChange={setHelpfulDialogOpen}
        data={helpfulData}
        onFix={handleHelpfulFix}
        isFixing={isFixingHelpful}
      />

      {/* Key Takeaways Drawer */}
      <KeyTakeawaysDrawer
        open={takeawaysDialogOpen}
        onOpenChange={setTakeawaysDialogOpen}
        data={takeawaysData}
        onAddToArticle={handleAddTakeawaysToArticle}
        isAddedToArticle={!!formState.keyTakeaways}
        onRemoveFromArticle={() => {
          // Remove takeaways box from content
          const content = formState.content || '';
          const cleaned = content.replace(/<div class="key-takeaways-box">[\s\S]*?<\/div>/gi, '').trim();
          updateFormField('content', cleaned);
          updateFormField('keyTakeaways', null);
        }}
      />

      {/* FAQ Drawer */}
      <ArticleFAQDrawer
        open={faqPanelOpen}
        onOpenChange={setFaqPanelOpen}
        focusKeyword={formState.focusKeyword}
        faqs={formState.faqs}
        slug={formState.slug}
        onUpdateFaqs={(faqs: ArticleFAQ[]) => updateFormField('faqs', faqs)}
        onInsertFAQSchema={(validatedFaqs: ArticleFAQ[]) => {
          const faqHtml = `
<div class="faq-section" itemscope itemtype="https://schema.org/FAQPage">
  <h2>Frequently Asked Questions</h2>
  ${validatedFaqs.map(faq => `
  <div itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
    <h3 itemprop="name">${faq.question}</h3>
    <div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
      <p itemprop="text">${faq.answer}</p>
    </div>
  </div>`).join('\n')}
</div>`;
          const newContent = formState.content + '\n' + faqHtml;
          updateFormField('content', newContent);
          toast.success(`Inserted ${validatedFaqs.length} FAQ(s) with schema markup`);
        }}
      />

      {/* Sources Drawer */}
      <ArticleSourcesDrawer
        open={sourcesPanelOpen}
        onOpenChange={setSourcesPanelOpen}
        sources={formState.sources}
        onUpdateSources={(sources) => updateFormField('sources', sources)}
        onSyncLinksFromArticle={handleSyncLinksFromArticle}
        brandId={selectedBrandId}
        articleTitle={formState.title}
      />

    </>
  );
}